import { useState, useCallback } from 'react';
import { Order, OrderStatus } from '../types';
import { ordersApi } from '../api/client';

// Simple hook-based store (avoids Zustand dep — React state is sufficient at this scale)
export function useOrderStore(restaurantId: string) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOrders = useCallback(
    async (status?: OrderStatus) => {
      setLoading(true);
      setError(null);
      try {
        const data = await ordersApi.list(restaurantId, status);
        setOrders(data);
      } catch (err) {
        console.error('[useOrderStore] fetchOrders failed:', err);
        setError('Failed to load orders. Check your network.');
      } finally {
        setLoading(false);
      }
    },
    [restaurantId]
  );

  const addOrUpdateOrder = useCallback((order: Order) => {
    setOrders((prev) => {
      const idx = prev.findIndex((o) => o.orderId === order.orderId);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = order;
        return updated;
      }
      return [order, ...prev]; // prepend new orders
    });
  }, []);

  const updateOrderStatus = useCallback(
    async (orderId: string, status: OrderStatus) => {
      try {
        const updated = await ordersApi.updateStatus(orderId, restaurantId, status);
        addOrUpdateOrder(updated);
        return updated;
      } catch (err) {
        console.error('[useOrderStore] updateOrderStatus failed:', err);
        throw err;
      }
    },
    [restaurantId, addOrUpdateOrder]
  );

  return {
    orders,
    loading,
    error,
    fetchOrders,
    addOrUpdateOrder,
    updateOrderStatus,
  };
}
